/**
 * リアルタイム予測データ管理フック
 * JRDBデータ連携・自動更新・WebSocket対応
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export function useLivePredictions() {
  const [predictions, setPredictions] = useState([]);
  const [metrics, setMetrics] = useState({
    return_rate: 0.9616,
    hit_rate: 0.1723,
    confidence: 0.94,
    total_bets: 290,
    profit: -3845,
    last_update: new Date().toISOString()
  });
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // WebSocket接続（メイン）
  const connectWebSocket = useCallback(() => {
    try {
      // 本番環境: wss://api.keiba-prediction.com/ws
      const wsUrl = process.env.NODE_ENV === 'production' 
        ? 'wss://api.keiba-prediction.com/ws'
        : 'ws://localhost:8080/ws';
      
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('🔗 WebSocket接続成功');
        setIsConnected(true);
        setError(null);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch(data.type) {
            case 'prediction_update':
              setPredictions(data.predictions);
              break;
            case 'metrics_update':
              setMetrics(prev => ({
                ...prev,
                ...data.metrics,
                last_update: new Date().toISOString()
              }));
              break;
            case 'jrdb_sync':
              console.log('📊 JRDBデータ同期:', data.status);
              break;
          }
        } catch (error) {
          console.error('WebSocketメッセージ解析エラー:', error);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('🔌 WebSocket接続切断');
        setIsConnected(false);
        // 5秒後に再接続
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocketエラー:', error);
        setError('リアルタイム接続エラー');
      };
      
    } catch (error) {
      console.error('WebSocket初期化エラー:', error);
      fallbackToPolling();
    }
  }, []);

  // フォールバック: ポーリング方式（Worker1新APIエンドポイント対応）
  const fallbackToPolling = useCallback(() => {
    console.log('📡 ポーリングモード（Worker1 JRDB API統合）に切替');
    
    const poll = async () => {
      try {
        const [predictionsRes, metricsRes, jrdbPredictionsRes] = await Promise.all([
          fetch('/api/predictions/live'),
          fetch('/api/simulation/current'),
          fetch('/api/predict/realtime', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              race_data: { race_id: 'current', horses: [] },
              jrdb_data: { horses: [] }
            })
          })
        ]);
        
        // Worker1新JRDB予測API統合
        if (jrdbPredictionsRes.ok) {
          const jrdbData = await jrdbPredictionsRes.json();
          console.log('🏇 Worker1 JRDB API統合成功:', jrdbData);
          if (jrdbData.predictions) {
            setPredictions(jrdbData.predictions);
          }
        }
        
        // 従来の予測API（フォールバック）
        if (predictionsRes.ok && !jrdbPredictionsRes.ok) {
          const predictionsData = await predictionsRes.json();
          setPredictions(predictionsData.predictions || []);
        }
        
        if (metricsRes.ok) {
          const metricsData = await metricsRes.json();
          setMetrics(prev => ({
            ...prev,
            ...metricsData,
            last_update: new Date().toISOString()
          }));
        }
        
        setError(null);
      } catch (error) {
        console.error('ポーリングエラー:', error);
        setError('データ取得エラー');
        
        // デモデータで継続
        useDemo();
      }
    };
    
    poll(); // 即座に実行
    pollIntervalRef.current = setInterval(poll, 30000); // 30秒ごと
  }, []);

  // デモデータ使用
  const useDemo = useCallback(() => {
    console.log('🎭 デモモードで継続');
    
    setPredictions([
      {
        race_id: 'DEMO_001',
        track: '東京',
        race_number: 11,
        race_name: '有馬記念（デモ）',
        time: '15:40',
        status: 'demo',
        horses: [
          {
            number: 1,
            name: 'エフフォーリア',
            jockey: '戸崎圭太',
            win_probability: 0.28,
            expected_odds: 3.2,
            confidence: 0.95,
            recommendation: 'strong_buy'
          },
          {
            number: 7,
            name: 'コントレイル',
            jockey: '福永祐一',
            win_probability: 0.24,
            expected_odds: 4.1,
            confidence: 0.92,
            recommendation: 'buy'
          }
        ]
      }
    ]);
    
    // デモ用のメトリクス更新
    const updateDemoMetrics = () => {
      setMetrics(prev => ({
        ...prev,
        return_rate: 0.9504 + (Math.random() - 0.5) * 0.02,
        hit_rate: 0.1701 + (Math.random() - 0.5) * 0.01,
        last_update: new Date().toISOString()
      }));
    };
    
    pollIntervalRef.current = setInterval(updateDemoMetrics, 15000);
  }, []);

  // Worker1新JRDB統合API強制同期
  const syncJRDBData = useCallback(async () => {
    try {
      console.log('🚀 Worker1 JRDB統合API同期開始...');
      
      // Worker1の新しいJRDB統合エンドポイント使用
      const response = await fetch('/api/predict/realtime', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          race_data: {
            race_id: 'sync_request',
            horses: []
          },
          jrdb_data: {
            sync_mode: true,
            horses: []
          }
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Worker1 JRDB統合API同期完了:', result);
        
        // 予測データ更新
        if (result.predictions) {
          setPredictions(result.predictions);
        }
        
        // メトリクス更新
        if (result.confidence) {
          setMetrics(prev => ({
            ...prev,
            confidence: result.confidence,
            model_version: result.model_version || 'v3.5.2-jrdb',
            last_update: new Date().toISOString()
          }));
        }
        
        return result;
      }
      
      // フォールバック: 従来のJRDB同期
      const fallbackResponse = await fetch('/api/jrdb/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (fallbackResponse.ok) {
        const fallbackResult = await fallbackResponse.json();
        console.log('📊 従来JRDB同期完了:', fallbackResult);
        return fallbackResult;
      }
    } catch (error) {
      console.error('JRDB同期エラー:', error);
    }
  }, []);

  // Worker1新API予測の手動更新
  const refreshPredictions = useCallback(async () => {
    try {
      console.log('🔄 Worker1 JRDB統合API予測更新開始...');
      
      // Worker1の新しいリアルタイム予測API使用
      const response = await fetch('/api/predict/realtime', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          race_data: {
            race_id: 'refresh_request',
            horses: []
          },
          jrdb_data: {
            refresh_mode: true,
            horses: []
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Worker1 API予測更新完了:', data);
        
        if (data.predictions) {
          setPredictions(data.predictions);
        }
        
        // 還元率向上確認
        if (data.model_version && data.model_version.includes('jrdb')) {
          console.log('🎯 JRDB統合モデル動作確認:', data.model_version);
        }
        
        return data;
      }
      
      // フォールバック: 従来の予測更新
      const fallbackResponse = await fetch('/api/predictions/refresh', {
        method: 'POST'
      });
      
      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        setPredictions(fallbackData.predictions || []);
        return fallbackData;
      }
    } catch (error) {
      console.error('予測更新エラー:', error);
    }
  }, []);

  // 初期化
  useEffect(() => {
    // WebSocket優先、失敗時はポーリング
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [connectWebSocket]);

  return {
    predictions,
    metrics,
    isConnected,
    error,
    syncJRDBData,
    refreshPredictions,
    // 接続状態
    connectionStatus: isConnected ? 'connected' : error ? 'error' : 'connecting'
  };
}